import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  signal,
  OnInit,
} from '@angular/core';
import {
  Assignment,
  AssignmentMontage,
  AssignmentService,
} from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { CardComponent } from '@ui/card/card.component';
import { finalize } from 'rxjs';

interface Document {
  subject_id: string;
  subject: string;
  type: 'aanvraag' | 'offerte' | 'technisch';
  created_at: string;
}

@Component({
  selector: 'app-assignments-service',
  standalone: true,
  imports: [CommonModule, CardComponent, DatePipe],
  template: `
    <ui-card [title]="'Unit'" [loading]="loading()">
      @for (service of service(); track service.project.id) {
      <div
        class="flex flex-col gap-3 border-b border-gray-200 pb-4 mb-4 last:border-b-0 last:mb-0 relative"
      >
        <div class="absolute top-0 right-0">
          <span [class]="getStatusClass(service.project.status)">
            {{ service.project.status | titlecase }}
          </span>
        </div>
        <div class="grid grid-cols-3 gap-y-3 text-sm">
          <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
            <span class="text-gray-600">Unit type:</span>
            <span class="font-medium">
              {{ service.project_details.description }}
            </span>
            <span class="text-gray-600">Unit fabrikant:</span>
            <span class="font-medium">
              {{ service.project_details.manufacturer }}
            </span>
          </div>

          @if (service.contracts.length > 0) {
          <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
            <span class="text-gray-600">Contract:</span>
            <span class="font-medium">
              @for (contract of service.contracts; track contract.id) {
              {{ contract.description }}
              }
            </span>
          </div>
          }
        </div>
      </div>
      }
    </ui-card>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class AssignmentsServiceComponent {
  assignmentsService = inject(AssignmentsService);
  assignment = input.required<Assignment | null>();
  service = signal<AssignmentService[] | null>(null);
  loading = signal<boolean>(false);

  constructor() {
    effect(
      () => {
        let assignmentId = this.assignment()?.id;
        if (assignmentId) {
          this.getService(assignmentId);
        }
      },
      { allowSignalWrites: true }
    );
  }

  getService(assignmentId: string) {
    this.loading.set(true);
    this.assignmentsService
      .getAssignmentService(assignmentId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((service) => {
        this.service.set(service);
      });
  }

  getStatusClass(status: string): string {
    const baseClasses = 'px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status.toLowerCase()) {
      case 'actief':
        return `bg-green-100 text-green-800 ${baseClasses}`;
      case 'realisatie':
        return `bg-yellow-100 text-yellow-800 ${baseClasses}`;
      default: // inactief
        return `bg-gray-100 text-gray-800 ${baseClasses}`;
    }
  }
}
